import { motion } from "framer-motion";
import { Calendar, Download, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface EventDetails {
  title: string;
  description: string;
  location: string;
  startDate: Date;
  endDate: Date;
  dateDisplay: string;
}

interface AddToCalendarDialogProps {
  children: React.ReactNode;
  eventDetails?: EventDetails;
}

const DEFAULT_EVENT_DETAILS: EventDetails = {
  title: "Winner's Cup 2026",
  description: "Join us for an unforgettable gymnastics experience at the Winner's Cup 2026! Featuring top gymnasts from across the nation.",
  location: "Ontario Convention Center, 2000 E Convention Center Way, Ontario, CA 91764",
  startDate: new Date("2026-02-27T08:00:00"),
  endDate: new Date("2026-03-01T18:00:00"),
  dateDisplay: "February 27 - March 1, 2026",
};

const formatDateForICS = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
};

const formatDateForGoogle = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
};

export const EVENT_2027_DETAILS: EventDetails = {
  title: "Winner's Cup 2027",
  description: "Join us for an unforgettable gymnastics experience at the Winner's Cup 2027! Featuring top gymnasts from across the nation.",
  location: "Ontario Convention Center, 2000 E Convention Center Way, Ontario, CA 91764",
  startDate: new Date("2027-02-26T08:00:00"),
  endDate: new Date("2027-02-28T18:00:00"),
  dateDisplay: "February 26-28, 2027",
};

const generateICSFile = (eventDetails: EventDetails): string => {
  const { title, description, location, startDate, endDate } = eventDetails;
  
  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Winner's Cup//Event//EN",
    "BEGIN:VEVENT",
    `DTSTART:${formatDateForICS(startDate)}`,
    `DTEND:${formatDateForICS(endDate)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  
  return icsContent;
};

const downloadICSFile = (eventDetails: EventDetails) => {
  const icsContent = generateICSFile(eventDetails);
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `winners-cup-${eventDetails.startDate.getFullYear()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const generateGoogleCalendarUrl = (eventDetails: EventDetails): string => {
  const { title, description, location, startDate, endDate } = eventDetails;
  
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`,
    details: description,
    location: location,
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const generateOutlookUrl = (eventDetails: EventDetails): string => {
  const { title, description, location, startDate, endDate } = eventDetails;
  
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: title,
    body: description,
    location: location,
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

const AddToCalendarDialog = ({ children, eventDetails = DEFAULT_EVENT_DETAILS }: AddToCalendarDialogProps) => {
  const calendarOptions = [
    {
      name: "Google Calendar",
      icon: "https://www.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png",
      action: () => window.open(generateGoogleCalendarUrl(eventDetails), "_blank"),
      type: "link" as const,
    },
    {
      name: "Outlook",
      icon: "https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg",
      action: () => window.open(generateOutlookUrl(eventDetails), "_blank"),
      type: "link" as const,
    },
    {
      name: "Apple Calendar",
      icon: null,
      action: () => downloadICSFile(eventDetails),
      type: "download" as const,
    },
    {
      name: "Download .ics",
      icon: null,
      action: () => downloadICSFile(eventDetails),
      type: "download" as const,
    },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md bg-secondary-secondary border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-primary-foreground font-display text-xl flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Add to Calendar
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          {/* Event Preview */}
          <div className="bg-secondary/50 border border-primary/10 rounded-lg p-4 mb-6">
            <h3 className="font-display text-lg text-primary-foreground mb-1">
              {eventDetails.title}
            </h3>
            <p className="text-primary-foreground/60 text-sm mb-2">
              {eventDetails.dateDisplay}
            </p>
            <p className="text-primary-foreground/50 text-xs">
              {eventDetails.location}
            </p>
          </div>
          
          {/* Calendar Options */}
          <div className="grid gap-2">
            {calendarOptions.map((option, index) => (
              <motion.div
                key={option.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Button
                  variant="outline"
                  className="w-full justify-between h-12 bg-secondary/30 border-primary/20 hover:bg-primary/10 hover:border-primary/40 transition-all group"
                  onClick={option.action}
                >
                  <span className="flex items-center gap-3">
                    {option.icon ? (
                      <img src={option.icon} alt="" className="w-5 h-5" />
                    ) : option.name === "Apple Calendar" ? (
                      <svg className="w-5 h-5 text-primary-foreground/70" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                    ) : (
                      <Download className="w-5 h-5 text-primary-foreground/70" />
                    )}
                    <span className="text-primary-foreground/80 group-hover:text-primary-foreground transition-colors">
                      {option.name}
                    </span>
                  </span>
                  {option.type === "link" ? (
                    <ExternalLink className="w-4 h-4 text-primary-foreground/40 group-hover:text-primary transition-colors" />
                  ) : (
                    <Download className="w-4 h-4 text-primary-foreground/40 group-hover:text-primary transition-colors" />
                  )}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToCalendarDialog;
